/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    addressOne: state.components.contactUsForm.fieldValues["addressOne"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressOneStyles {
    addressOneTextField?: TextFieldPresentationProps;
}

export const addressOneStyles: AddressOneStyles = {
    addressOneTextField: {
        cssOverrides: {
            formField: css`
                margin-bottom: 15px;
                width: 45%;
                @media (max-width: 768px) {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = addressOneStyles;

const AddressOne: React.FC<Props> = ({ fields, addressOne, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [addressOneErrorMessage, setAddressOneErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.addressOne = validateAddressOne;
        if (typeof addressOne !== "undefined") {
            validateAddressOne();
        }        
    }, [addressOne]);

    React.useEffect(() => {
        contactFormContext.validators.addressOne = validateAddressOne;
    }, [isRequired]);

    const validateAddressOne = () => {
        const errorMessage = isRequired && !addressOne ? (siteMessage("Field_Required", label) as string) : "";
        setAddressOneErrorMessage(errorMessage);
        return !errorMessage;
    };

    const addressOneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "addressOne", value: event.target.value });
    };


    return (
        <TextField
            {...styles.addressOneTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={addressOne || ""}
            error={addressOneErrorMessage}
            minLength={1}
            maxLength={100}
            onChange={addressOneChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressOne),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Address One",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Address One",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
